import { CircleHelp, Lightbulb, Mic, X } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { HERO_CHIPS, PALCO_HERO_SNAPSHOT } from "@/data/landingContent";
import { cn } from "@/lib/utils";

const CHIP_SLOTS = [
  { className: "left-0 top-16", delay: 0 },
  { className: "right-0 top-[42%]", delay: 0.35 },
  { className: "left-1 bottom-24", delay: 0.7 },
] as const;

const GLASS = "border border-white/15 bg-white/10 text-white backdrop-blur-md";

export const PhoneMockup = () => {
  const reduce = useReducedMotion();

  return (
    <div className="relative mx-auto w-full max-w-[520px] lg:px-2">
      <div className="landing-field landing-field-soft" aria-hidden="true" />

      {HERO_CHIPS.map((chip, index) => (
        <FloatingChip
          key={chip.label}
          label={chip.label}
          tone={chip.tone}
          className={CHIP_SLOTS[index].className}
          delay={CHIP_SLOTS[index].delay}
          reduce={Boolean(reduce)}
        />
      ))}

      <div className="relative z-10 mx-auto w-[min(100%,328px)]" aria-hidden>
        <div className="overflow-hidden rounded-[44px] border-[8px] border-neutral-900 bg-blue-900 shadow-[0_28px_70px_rgba(15,23,42,0.22)]">
          <div className="relative px-3.5 pb-4 pt-2.5 text-white">
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <div className="absolute -top-[18%] left-1/2 h-[72%] w-[150%] -translate-x-1/2 bg-[radial-gradient(ellipse_at_center,rgb(22_83_230_/_0.4)_0%,transparent_72%)]" />
              <div className="absolute -bottom-[8%] -right-[18%] h-[48%] w-[80%] bg-[radial-gradient(ellipse_at_center,rgb(255_122_26_/_0.16)_0%,transparent_72%)]" />
              <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgb(4_10_24_/_0.28)_0%,rgb(4_10_24_/_0.5)_38%,rgb(4_10_24_/_0.88)_100%)]" />
            </div>

            <div className="relative flex items-center justify-between px-1 text-[11px] font-semibold">
              <span>9:41</span>
              <span className="h-4 w-[4.5rem] rounded-full bg-neutral-900" />
              <span>5G</span>
            </div>

            <header className="relative mt-3 flex items-center gap-1.5">
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${GLASS}`}>
                <X className="h-3.5 w-3.5" strokeWidth={1.75} />
              </span>
              <p className={`min-w-0 flex-1 rounded-full px-2 py-1.5 text-center text-[11px] font-medium leading-tight ${GLASS}`}>
                {PALCO_HERO_SNAPSHOT.goal}
              </p>
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${GLASS}`}>
                <CircleHelp className="h-3.5 w-3.5" strokeWidth={1.75} />
              </span>
            </header>

            <div className="relative mt-5 flex flex-col items-center">
              <div className="overflow-hidden rounded-full border border-white/20 bg-white p-1">
                <img
                  src={PALCO_HERO_SNAPSHOT.stageArt}
                  alt=""
                  className="h-[132px] w-[132px] rounded-full object-cover object-[center_18%]"
                />
              </div>
              <p className="mt-2 text-[11px] leading-tight text-white/70">
                {PALCO_HERO_SNAPSHOT.partnerLabel}
              </p>
            </div>

            <div className={`relative mt-4 rounded-[20px] px-3.5 py-3 ${GLASS}`}>
              <p className="text-[13px] font-bold leading-snug">
                &ldquo;{PALCO_HERO_SNAPSHOT.partnerOpen}&rdquo;
              </p>
              <p className="mt-1.5 text-[11px] leading-snug text-blue-100">
                {PALCO_HERO_SNAPSHOT.helper}
              </p>
            </div>

            <div className="relative mt-4 flex items-center justify-center gap-4 pb-1">
              <span className={`flex h-10 w-10 items-center justify-center rounded-full ${GLASS}`}>
                <X className="h-4 w-4" strokeWidth={1.75} />
              </span>
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-500 text-white shadow-[0_10px_24px_rgba(43,107,255,0.5)]">
                <Mic className="h-7 w-7" strokeWidth={1.75} />
              </span>
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-600 text-white shadow-[0_6px_16px_rgba(255,122,26,0.4)]">
                <Lightbulb className="h-4 w-4" strokeWidth={1.75} />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FloatingChip = ({
  label,
  tone,
  className,
  delay,
  reduce,
}: {
  label: string;
  tone: "blue" | "orange";
  className: string;
  delay: number;
  reduce: boolean;
}) => (
  <motion.span
    className={cn(
      "pointer-events-none absolute z-20 hidden rounded-full border bg-white/95 px-3 py-1.5 text-xs font-semibold shadow-[0_8px_18px_rgba(15,23,42,0.08)] lg:inline-flex",
      tone === "orange" ? "border-orange-300 text-orange-700" : "border-blue-200 text-blue-600",
      className,
    )}
    animate={reduce ? undefined : { y: [0, -8, 0] }}
    transition={reduce ? undefined : { duration: 4.2, repeat: Infinity, ease: "easeInOut", delay }}
  >
    {label}
  </motion.span>
);
